"use client";

import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function ScrollNavButtons() {
  // Не показуємо кнопки одразу при відкритті — тільки після невеликого скролу.
  const revealAfterScrollPx = 200;
  const thresholdTopPx = 420;
  const thresholdBottomPx = 420;
  const [showTop, setShowTop] = useState(false);
  const [showBottom, setShowBottom] = useState(false);

  const motionReduced = useMemo(() => prefersReducedMotion(), []);

  useEffect(() => {
    let raf = 0;

    const update = () => {
      const y = window.scrollY;
      const h = window.innerHeight;
      const doc = document.documentElement;
      const max = Math.max(0, doc.scrollHeight - h);
      const distToBottom = Math.max(0, max - y);

      const isScrollable = max > 0;
      const revealed = y > revealAfterScrollPx;

      setShowTop(isScrollable && y > thresholdTopPx);
      setShowBottom(
        isScrollable && revealed && distToBottom > thresholdBottomPx
      );
    };

    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        update();
      });
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const btnBase =
    "h-11 w-11 rounded-lg border border-[#2a475e] bg-[#16202d]/90 backdrop-blur " +
    "text-[#c7d5e0] shadow-lg shadow-black/25 " +
    "hover:bg-[#1f2e3f] hover:text-white hover:border-[#66c0f4]/60 " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#66c0f4]/40 focus-visible:ring-offset-0 " +
    "transition-all duration-200";

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col gap-2">
      <button
        type="button"
        onClick={() => {
          const max = Math.max(
            0,
            document.documentElement.scrollHeight - window.innerHeight
          );
          window.scrollTo({
            top: max,
            behavior: motionReduced ? "auto" : "smooth",
          });
        }}
        aria-label="Scroll to bottom"
        className={[
          btnBase,
          showBottom
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-2 pointer-events-none",
        ].join(" ")}
      >
        <ChevronDown className="h-5 w-5 mx-auto" />
      </button>

      <button
        type="button"
        onClick={() => {
          window.scrollTo({
            top: 0,
            behavior: motionReduced ? "auto" : "smooth",
          });
        }}
        aria-label="Scroll to top"
        className={[
          btnBase,
          showTop
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-2 pointer-events-none",
        ].join(" ")}
      >
        <ChevronUp className="h-5 w-5 mx-auto" />
      </button>
    </div>
  );
}
